import type { Language } from './kioskText'
import type { StaffLanguage } from './staffText'
import type { MediaSkip, ScreenCache, Unit } from './types'

/**
 * Whether a board shows a given desk's calls.
 *
 * A board with no desks chosen shows every one, so a branch upgrading to this
 * version sees exactly what it saw before and nobody has to configure something to
 * get back to where they were.
 *
 * The same rule lives on the server, in org.Unit.ShowsDesk. Two copies, because
 * the server decides what a board is allowed to be given and the board decides what
 * to draw, and neither can be the only one that knows.
 */
export function showsDesk(unit: Unit | null | undefined, counterId: string | undefined): boolean {
  const desks = unit?.desks ?? []
  if (desks.length === 0) return true
  return Boolean(counterId && desks.includes(counterId))
}

/**
 * The language a hall board reads in: its machine's, the same setting its voice
 * uses, so that a board never shows one language and says another.
 *
 * Undefined when the machine has none — and that answer matters: a board nobody
 * has set shows its own words in English and its designed text in Azerbaijani,
 * exactly as every board did before there was a choice. Nothing on a wall changes
 * on upgrade until somebody chooses a language for it.
 */
export function boardLanguage(unit: Pick<Unit, 'voice'> | null | undefined): Language | undefined {
  const language = unit?.voice?.language
  return language === 'az' || language === 'ru' || language === 'en' ? language : undefined
}

/**
 * The desk a feedback tablet stands at: its one desk, or nothing. The server
 * refuses a tablet at no desk or two (org.Unit.Validate), so "nothing" is a
 * machine of another kind, or one read from a server older than this.
 */
export function feedbackDesk(unit: Pick<Unit, 'kind' | 'desks'> | null | undefined): string | undefined {
  if (unit?.kind !== 'feedback' || unit.desks.length !== 1) return undefined
  return unit.desks[0]
}

/** The server's refusals that mean this address was taken out of service — the
 *  machine or its branch (resolveScreen in internal/api/screen.go answers 410) —
 *  and not that the link is down. */
const RETIRED_CODES: readonly unknown[] = ['RETIRED', 'BRANCH_RETIRED']

/** Whether a failed question about this screen was the server saying the machine,
 *  or its branch, has been taken out of service. */
export function isRetiredRefusal(err: unknown): boolean {
  return RETIRED_CODES.includes((err as { code?: unknown } | null | undefined)?.code)
}

/**
 * Whether an addressed screen is retired, after its latest question about itself:
 * `err` is what the question failed with, or undefined when it was answered.
 *
 * An answer means the machine is in service, so one put back into service
 * recovers by itself at its next question. A failure that is not the retired
 * refusal — the server unreachable, a timeout — says nothing either way, so the
 * screen keeps what it knew.
 */
export function retiredAfter(was: boolean, err: unknown): boolean {
  if (err === undefined) return false
  return isRetiredRefusal(err) ? true : was
}

/** The server's refusals of a machine that brought no key, or not its current one. */
const KEY_CODES: readonly unknown[] = ['SCREEN_KEY_REQUIRED', 'SCREEN_KEY_REFUSED']

/** Whether an addressed screen lacks its key, after its latest question about itself;
 *  the retiredAfter rule: an answer clears it, a failure of another kind keeps it. */
export function keyRefusedAfter(was: boolean, err: unknown): boolean {
  if (err === undefined) return false
  return KEY_CODES.includes((err as { code?: unknown } | null | undefined)?.code) ? true : was
}

/** What a hall TV last skipped because it would not play; null for anything else, or nothing said. */
export function mediaSkipped(unit: Pick<Unit, 'kind' | 'mediaSkipped'>): MediaSkip | null {
  return unit.kind === 'display' && unit.mediaSkipped ? unit.mediaSkipped : null
}

/** A kiosk whose printer last failed or did not answer, with when and why; null
 *  for one that printed since, or never said. */
export function printerFault(unit: Pick<Unit, 'kind' | 'printerState'>): { absent: boolean; at: string; error: string } | null {
  const p = unit.printerState
  if (unit.kind !== 'kiosk' || !p || p.state === 'ready') return null
  return { absent: p.state === 'absent', at: p.errorAt ?? p.at, error: p.error ?? '' }
}

/** What a screen last said its worker holds; null until it says. */
export function screenCacheOf(unit: Pick<Unit, 'screenCache'>): ScreenCache | null {
  return unit.screenCache ?? null
}

/** A cache size as its row says it: whole megabytes under a gigabyte, else gigabytes to a tenth. */
export function cacheAmount(bytes: number, lang: StaffLanguage): { key: 'catalogue.units.mb' | 'catalogue.units.gb'; n: string } {
  if (bytes < 1 << 30) return { key: 'catalogue.units.mb', n: String(Math.round(bytes / (1 << 20))) }
  const n = String(Math.round((bytes / (1 << 30)) * 10) / 10)
  return { key: 'catalogue.units.gb', n: lang === 'en' ? n : n.replace('.', ',') }
}
